import React from 'react';
import { Copy, Trash2, Server, AlertTriangle, Columns3 } from 'lucide-react';
import { DatacenterConfig } from '../../hooks/useDataCenterApi';
import { useEditorStore, AisleType, PPM } from '../../store/editorStore';
import { PropertiesEditor } from './PropertiesEditor';
import { TrayInspector } from './TrayInspector';
import { useShallow } from 'zustand/react/shallow';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../utils/api';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const AISLE_TYPE_OPTIONS: { value: AisleType; label: string; title?: string; varColor: string; varBg: string; varBorder: string }[] = [
  { value: 'cold',        label: 'Холодный коридор', varColor: 'var(--color-cold)', varBg: 'var(--color-cold-bg)', varBorder: 'var(--color-cold-border)' },
  { value: 'hot',         label: 'Горячий коридор',  varColor: 'var(--color-hot)', varBg: 'var(--color-hot-bg)', varBorder: 'var(--color-hot-border)' },
  { value: 'containment', label: 'Containment',      varColor: 'var(--color-containment)', varBg: 'var(--color-containment-bg)', varBorder: 'var(--color-containment-border)' },
];

// ─── Shared KV row ─────────────────────────────────────────────────────────────
function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: 11 }}>{label}</span>
      <span style={{ color: 'var(--text-secondary, #9ca3af)', fontSize: 11, fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </>
  );
}

// ─── Main sidebar ──────────────────────────────────────────────────────────────
export function PropertiesSidebar({ dc, dcCode, draftId }: { dc: DatacenterConfig; dcCode: string; draftId?: number | null }) {
  const {
    selectedIds, racks, aisleZones, telemetryData,
    updateAisleZone, removeAisleZone, convertAisleZoneToOperational,
    trayNodes, addTrayNode,
    selectedEdgeId, setSelectedEdgeId, trayEdges, updateTrayEdge, deleteTrayEdge,
  } = useEditorStore(useShallow(state => ({
    selectedIds:     state.selectedIds,
    racks:           state.racks,
    aisleZones:      state.aisleZones,
    telemetryData:   state.telemetryData,
    updateAisleZone: state.updateAisleZone,
    removeAisleZone: state.removeAisleZone,
    convertAisleZoneToOperational: state.convertAisleZoneToOperational,
    trayNodes:       state.trayNodes,
    addTrayNode:     state.addTrayNode,
    selectedEdgeId:  state.selectedEdgeId,
    setSelectedEdgeId: state.setSelectedEdgeId,
    trayEdges:       state.trayEdges,
    updateTrayEdge:  state.updateTrayEdge,
    deleteTrayEdge:  state.deleteTrayEdge,
  })));

  const { activeWorkspaceId } = useWorkspace();
  const { data: trayOccData } = useQuery({
    queryKey: ['trayOccupancy', dcCode, activeWorkspaceId],
    queryFn: async () => {
      const res = await api.get(`/api/datacenters/${dcCode}/trays/occupancy`, {
        params: { workspace: activeWorkspaceId },
      });
      return res.data;
    },
    refetchInterval: 10000,
  });
  const edgeCounts = trayOccData?.edge_counts || {};
  const { data: capacityAuditData } = useQuery({
    queryKey: ['trayCapacityAudit', dcCode, activeWorkspaceId],
    queryFn: async () => (await api.get(`/api/datacenters/${dcCode}/trays/capacity-audit`, {
      params: { workspace: activeWorkspaceId },
    })).data,
    refetchInterval: 10000,
  });
  const [impactPreview, setImpactPreview] = React.useState<{ affected_routes?: Array<{ cable_id: string; edge_ids: string[]; uncertain?: boolean; blocking?: boolean }> } | null>(null);
  const selectedId   = selectedIds.length === 1 ? selectedIds[0] : null;
  const selectedRack = selectedId ? racks[selectedId] : null;
  const selectedZone = selectedId ? aisleZones[selectedId] : null;
  const [overrideReason, setOverrideReason] = React.useState('');
  const [batchRowError, setBatchRowError] = React.useState('');
  React.useEffect(() => {
    setOverrideReason(selectedZone?.typeOverride?.reason || '');
  }, [selectedZone?.id, selectedZone?.typeOverride?.reason]);

  const selectedEdge = selectedEdgeId ? trayEdges[selectedEdgeId] : null;
  if (selectedEdge) {
    const sourceNode = trayNodes[selectedEdge.source];
    const targetNode = trayNodes[selectedEdge.target];
    const key1 = `${selectedEdge.source}_${selectedEdge.target}`;
    const key2 = `${selectedEdge.target}_${selectedEdge.source}`;
    const cableCount = edgeCounts[key1] || edgeCounts[key2] || selectedEdge.usedCables || 0;

    return (
      <TrayInspector
        edge={selectedEdge}
        sourceNode={sourceNode}
        targetNode={targetNode}
        allEdges={trayEdges}
        allNodes={trayNodes}
        cableCount={cableCount}
        onUpdate={updateTrayEdge}
        onUpdateNode={addTrayNode}
        onDelete={deleteTrayEdge}
        onClose={() => setSelectedEdgeId(null)}
        impactPreview={impactPreview}
        capacityAudit={capacityAuditData?.segments?.find((segment: { edge_id?: string }) => segment.edge_id === selectedEdge.id) || null}
        onPreview={async updates => {
          const nextEdges = { ...trayEdges, [selectedEdge.id]: { ...selectedEdge, ...updates } };
          try {
            if (!draftId) return;
            const settings = useEditorStore.getState().dcSettings;
            const defaultZ = (settings?.defaultRackHeightM ?? 2.0) + (settings?.defaultTrayHeightM ?? 0.3);
            const nodes = Object.values(trayNodes).map(node => {
              const z = node.z_m ?? defaultZ;
              return { ...node, x: Number((node.x / PPM).toFixed(3)), y: Number((node.y / PPM).toFixed(3)), z, z_m: z };
            });
            const response = await api.post(`/api/datacenters/${dcCode}/drafts/${draftId}/pathway-impact-preview`, { cable_trays_json: { nodes, edges: Object.values(nextEdges) } });
            setImpactPreview(response.data);
          } catch {
            setImpactPreview(null);
          }
        }}
      />
    );
  }

  const tele         = selectedRack ? telemetryData[selectedRack.id] : null;

  // ── Multi-select → batch actions ──
  if (selectedIds.length > 1) {
    const selectedRacks = selectedIds.map(id => racks[id]).filter(Boolean);
    const rackCount = selectedRacks.length;
    const zoneCount = selectedIds.length - rackCount;
    return (
      <div style={{ padding: '12px', fontSize: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
          Групповые действия
        </div>
        
        {/* Count summary badge */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          background: 'var(--color-accent-blue-bg, rgba(59, 130, 246, 0.1))',
          border: '1px solid var(--color-accent-blue-border, rgba(59, 130, 246, 0.25))',
          borderRadius: 6,
          marginBottom: 14,
          fontSize: 11,
          color: 'var(--color-accent-blue, #60a5fa)',
          fontWeight: 500,
        }}>
          <Server size={13} />
          <span>
            Выделено: <strong style={{ fontFamily: 'var(--font-data)' }}>{rackCount}</strong> стоек
            {zoneCount > 0 && <>, <strong style={{ fontFamily: 'var(--font-data)' }}>{zoneCount}</strong> зон</>}
          </span>
        </div>

        {/* Batch assign row */}
        {rackCount > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Назначить ряд
            </label>
            <select
              style={{
                width: '100%',
                background: 'var(--bg-input, #1f2937)',
                border: '1px solid var(--border-color, #374151)',
                borderRadius: 5,
                padding: '5px 8px',
                fontSize: 12,
                color: 'var(--text-primary, #e2e8f0)',
                cursor: 'pointer',
                outline: 'none',
              }}
              value=""
              onChange={e => {
                const rowId = e.target.value || undefined;
                const itRackIds = selectedRacks.filter(r => !r.isFacility && !r.isBlocked).map(r => r.id);
                setBatchRowError('');
                if (rowId && itRackIds.length) {
                  const assigned = useEditorStore.getState().assignRacksToRow(itRackIds, rowId);
                  if (!assigned) setBatchRowError('Стойки не назначены: проверьте занятые, зарезервированные и пересекающиеся позиции.');
                }
              }}
            >
              <option value="">— Выберите ряд назначения —</option>
              {Object.values(useEditorStore.getState().rows).sort((a, b) => a.labelIndex - b.labelIndex).map(row => (
                <option key={row.id} value={row.id}>{row.name}</option>
              ))}
            </select>
            {batchRowError && <div role="alert" style={{ color: 'var(--status-critical)', fontSize: 10, marginTop: 5 }}>{batchRowError}</div>}
          </div>
        )}

        {/* Batch power limit */}
        {rackCount > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Лимит мощности для всех (кВт)
            </label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="number"
                  step={0.5}
                  min={0}
                  placeholder="5.0"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'var(--bg-input, #1f2937)',
                    border: '1px solid var(--border-color, #374151)',
                    borderRadius: 5,
                    padding: '5px 8px',
                    fontSize: 12,
                    fontFamily: 'var(--font-data)',
                    color: 'var(--text-primary, #e2e8f0)',
                    outline: 'none',
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const kw = parseFloat((e.target as HTMLInputElement).value);
                      if (!isNaN(kw)) {
                        const itRackIds = selectedRacks.filter(r => !r.isFacility && !r.isBlocked).map(r => r.id);
                        if (itRackIds.length) useEditorStore.getState().batchUpdateRacks(itRackIds, { max_power_w: Math.round(kw * 1000) });
                      }
                    }
                  }}
                />
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', background: 'var(--bg-hover)', padding: '2px 5px', borderRadius: 3, border: '1px solid var(--border, #374151)', fontFamily: 'var(--font-data)' }}>
                ↵ Enter
              </span>
            </div>
          </div>
        )}

        {/* Batch actions buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4, borderTop: '1px solid var(--border, rgba(255,255,255,0.06))' }}>
          <button
            onClick={() => useEditorStore.getState().duplicateSelected()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: '100%',
              padding: '6px 10px',
              background: 'var(--color-accent-blue-bg, rgba(59, 130, 246, 0.1))',
              border: '1px solid var(--color-accent-blue-border, rgba(59, 130, 246, 0.3))',
              borderRadius: 5,
              color: 'var(--color-accent-blue, #60a5fa)',
              fontSize: 11,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.12s ease',
            }}
          >
            <Copy size={12} />
            <span>Дублировать выделенное (Ctrl+D)</span>
          </button>
          <button
            onClick={() => useEditorStore.getState().deleteSelected()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: '100%',
              padding: '6px 10px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 5,
              color: 'var(--status-critical, #ef4444)',
              fontSize: 11,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.12s ease',
            }}
          >
            <Trash2 size={12} />
            <span>Удалить выделенное (Del)</span>
          </button>
        </div>
      </div>
    );
  }

  // ── Nothing selected → show DC settings (DcInspector) ──
  if (!selectedId) {
    return (
      <div style={{ fontSize: 12 }}>
        <div style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            {dc.name}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px' }}>
            <KV label="Рядов" value={dc.rows_count} />
            <KV label="Стоек в ряду" value={dc.max_racks_per_row} />
            <KV label="Шаг стоек" value={`${dc.rack_step_m} м`} />
            <KV label="Проход" value={`${dc.aisle_width_m} м`} />
            <KV label="Глубина" value={`${dc.rack_depth_m} м`} />
            <KV label="Высота" value={`${dc.rack_height_m} м`} />
          </div>
        </div>
        {/* DcInspector — editor settings, row management, naming masks */}
        <PropertiesEditor selectedId={null} dcCode={dcCode} />
      </div>
    );
  }

  // ── Aisle zone selected ──
  if (selectedZone) {
    const areaM2 = ((selectedZone.w / 100) * (selectedZone.h / 100)).toFixed(1);
    return (
      <div style={{ padding: '12px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
          Коридор
        </div>

        {/* Type selector */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 4 }}>Тип коридора</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {AISLE_TYPE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                disabled={selectedZone.derivedStatus === 'indeterminate' || (Boolean(selectedZone.derivedFromRowIds) && (opt.value === 'containment' || !overrideReason.trim()))}
                onClick={() => {
                  if (selectedZone.derivedFromRowIds) {
                    updateAisleZone(selectedZone.id, {
                      typeOverride: { aisleType: opt.value as 'cold' | 'hot', reason: overrideReason.trim() },
                    });
                  } else {
                    updateAisleZone(selectedZone.id, { aisleType: opt.value, typeOverride: undefined });
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 8px', borderRadius: 5, border: '1px solid',
                  cursor: 'pointer', fontSize: 12, textAlign: 'left',
                  opacity: selectedZone.derivedStatus === 'indeterminate' || (Boolean(selectedZone.derivedFromRowIds) && (opt.value === 'containment' || !overrideReason.trim())) ? 0.5 : 1,
                  background: (selectedZone.typeOverride?.aisleType ?? selectedZone.aisleType) === opt.value ? opt.varBg : 'transparent',
                  borderColor: (selectedZone.typeOverride?.aisleType ?? selectedZone.aisleType) === opt.value ? opt.varBorder : 'var(--bg-hover)',
                  color: (selectedZone.typeOverride?.aisleType ?? selectedZone.aisleType) === opt.value ? opt.varColor : 'var(--text-secondary, #9ca3af)',
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 2, background: opt.varColor, flexShrink: 0 }} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {selectedZone.derivedFromRowIds && selectedZone.derivedStatus !== 'indeterminate' && (
          <div style={{ marginBottom: 12, padding: 8, border: '1px solid rgba(251,191,36,0.35)', background: 'rgba(251,191,36,0.06)', borderRadius: 5 }}>
            <div style={{ fontSize: 10, color: '#fbbf24', fontWeight: 700, marginBottom: 5 }}>Исключение типа</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 5 }}>
              Укажите причину, чтобы заменить вычисленный тип. Исключение останется предупреждением.
            </div>
            <input
              type="text"
              value={overrideReason}
              onChange={e => setOverrideReason(e.target.value)}
              placeholder="Причина отклонения"
              aria-label="Причина исключения типа коридора"
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-hover)', border: '1px solid var(--border-color)', borderRadius: 5, padding: '5px 8px', fontSize: 12, color: 'var(--text-primary, #e2e8f0)' }}
            />
          </div>
        )}

        {selectedZone.derivedStatus === 'indeterminate' && selectedZone.derivedReason && (
          <div style={{ marginBottom: 12, padding: 8, border: '1px solid rgba(248,113,113,0.45)', background: 'rgba(239,68,68,0.08)', borderRadius: 5, color: '#fca5a5', fontSize: 11 }}>
            Ошибка: {selectedZone.derivedReason}
          </div>
        )}

        {/* Label input */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 4 }}>Метка</label>
          <input
            type="text"
            value={selectedZone.label || ''}
            onChange={e => updateAisleZone(selectedZone.id, { label: e.target.value })}
            placeholder="напр. Холодный A"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--bg-hover)',
              border: '1px solid var(--border-color)',
              borderRadius: 5, padding: '5px 8px',
              fontSize: 12, color: 'var(--text-primary, #e2e8f0)',
            }}
          />
        </div>

        {/* Position & Dimensions (editable) */}
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Позиция и размеры</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 2 }}>X (м)</label>
            <input type="number" step={0.1}
              value={Math.round(selectedZone.x / 100 * 10) / 10}
              onChange={e => updateAisleZone(selectedZone.id, { x: Math.round(parseFloat(e.target.value) * 100) })}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-hover)', border: '1px solid var(--border-color)', borderRadius: 5, padding: '4px 6px', fontSize: 12, color: 'var(--text-primary, #e2e8f0)' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 2 }}>Y (м)</label>
            <input type="number" step={0.1}
              value={Math.round(selectedZone.y / 100 * 10) / 10}
              onChange={e => updateAisleZone(selectedZone.id, { y: Math.round(parseFloat(e.target.value) * 100) })}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-hover)', border: '1px solid var(--border-color)', borderRadius: 5, padding: '4px 6px', fontSize: 12, color: 'var(--text-primary, #e2e8f0)' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 2 }}>Ширина (м)</label>
            <input type="number" step={0.1} min={0.3}
              value={Math.round(selectedZone.w / 100 * 10) / 10}
              onChange={e => updateAisleZone(selectedZone.id, { w: Math.round(parseFloat(e.target.value) * 100) })}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-hover)', border: '1px solid var(--border-color)', borderRadius: 5, padding: '4px 6px', fontSize: 12, color: 'var(--text-primary, #e2e8f0)' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 2 }}>Глубина (м)</label>
            <input type="number" step={0.1} min={0.3}
              value={Math.round(selectedZone.h / 100 * 10) / 10}
              onChange={e => updateAisleZone(selectedZone.id, { h: Math.round(parseFloat(e.target.value) * 100) })}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-hover)', border: '1px solid var(--border-color)', borderRadius: 5, padding: '4px 6px', fontSize: 12, color: 'var(--text-primary, #e2e8f0)' }}
            />
          </div>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', marginBottom: 12 }}>
          Площадь: {areaM2} м²
        </div>

        {!selectedZone.derivedFromRowIds && (
          <button
            onClick={() => convertAisleZoneToOperational(selectedZone.id)}
            style={{ width: '100%', padding: '6px', borderRadius: 5, border: '1px solid rgba(251,191,36,0.4)', background: 'rgba(251,191,36,0.08)', color: '#fbbf24', fontSize: 12, cursor: 'pointer', marginBottom: 6 }}
          >
            Преобразовать в операционную зону
          </button>
        )}

        <button
          onClick={() => removeAisleZone(selectedZone.id)}
          style={{
            width: '100%', padding: '6px', borderRadius: 5,
            border: '1px solid var(--status-critical)',
            background: 'rgba(239,68,68,0.08)',
            color: 'var(--status-critical)', fontSize: 12, cursor: 'pointer',
          }}
        >
          Удалить зону
        </button>
      </div>
    );
  }

  // ── Rack / Facility selected ──
  if (selectedRack) {
    const estimated = selectedRack.widthM == null || selectedRack.depthM == null;
    const areaM2 = ((selectedRack.widthM ?? selectedRack.width / 100) * (selectedRack.depthM ?? selectedRack.height / 100)).toFixed(2);
    const isFac = Boolean(selectedRack.isFacility);
    const isPillar = Boolean(selectedRack.isBlocked || selectedRack.rackType === 'pillar');

    return (
      <div>
        <div style={{ padding: '12px 12px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          {isPillar ? 'Колонна' : isFac ? 'Инфраструктура' : 'Стойка'}
        </div>

        {/* Rack editor fields */}
        <PropertiesEditor selectedId={selectedId} dcCode={dcCode} />

        {/* Physical dimensions */}
        <div style={{ padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 8px' }}>
            <KV label={estimated ? 'Площадь оценочная' : 'Площадь'} value={<span style={{ fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums' }}>{areaM2} м²</span>} />
            {!isFac && !isPillar && (
              <KV label="U-слоты" value={<span style={{ fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums' }}>{selectedRack.uCount}</span>} />
            )}
          </div>
        </div>

        {/* Telemetry */}
        {tele && (
          <div style={{ margin: '0 12px 8px', padding: '10px', background: 'var(--color-accent-blue-bg)', border: '1px solid var(--color-accent-blue-border)', borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>Телеметрия</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px', fontSize: 12 }}>
              {tele.temperature !== undefined && (
                <>
                  <span style={{ color: 'var(--text-muted, #6b7280)' }}>Температура</span>
                  <span style={{ color: tele.temperature > 35 ? 'var(--status-critical)' : tele.temperature > 28 ? 'var(--status-warning)' : 'var(--status-normal)', fontWeight: 600, fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums' }}>
                    {tele.temperature.toFixed(1)} °C
                  </span>
                </>
              )}
              {tele.powerLoad !== undefined && (
                <>
                  <span style={{ color: 'var(--text-muted, #6b7280)' }}>Мощность</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums' }}>
                    {tele.powerLoad.toFixed(1)} / {tele.powerCapacity} кВт
                  </span>
                </>
              )}
              {tele.hasSpof && (
                <span style={{ color: 'var(--status-warning)', gridColumn: '1 / -1', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <AlertTriangle size={12} /> SPOF
                </span>
              )}
            </div>
          </div>
        )}

        {selectedIds.length > 1 && (
          <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-color, #1f2937)', marginTop: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)', marginBottom: 6 }}>
              Выделено: {selectedIds.length} объектов
            </div>
            <button
              onClick={() => useEditorStore.getState().setShowRackAllocation(true)}
              style={{
                width: '100%',
                padding: '6px 10px',
                background: 'rgba(168, 85, 247, 0.12)',
                border: '1px solid rgba(168, 85, 247, 0.35)',
                color: '#c084fc',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <Columns3 size={13} /> Распределить по группам (M)
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
}
